/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import { Get, GetCached, Post, Delete, Put, Patch } from "./apiHelper";
import { ReverseMap } from "../helpers/types";
import { Assessment, ICD } from "../interfaces/AssessmentInterfaces";
import { AttachmentInterface } from "../interfaces/AttachmentInterface";

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

const servicesRoot = "/api/symptoms";
const diagnosisRoot = "/api/icd";
const labTest = "/api/labTests1";
const assessmentsRoot = "/api/assessments";
const assessmentRoot = "/api/assessment";
const assessmentTemplateRoot = "/api/assessment/template";
const assessmentTemplateImagesRoot = "/api/assessment-template-images";

export const assessmentAttachmentsUrl = (assessmentId: number): string => {
  if (assessmentId) {
    return `/api/attachments/assessment/${assessmentId}`;
  }
  return `/api/attachments/assessment`;
};

export const deleteAttachmentEndPoint = (): string => `/api/attachments/assessment`;

export async function getBatchSymptoms(forceUpdate?: boolean): Promise<any> {
  const response = await GetCached({
    url: servicesRoot,
    forceUpdate
  });
  return response.data;
}

export async function getBatchDiagnosis(): Promise<ICD[]> {
  const response = await Get(`${diagnosisRoot}`, true);

  return response.data as ICD[];
}

export async function getLabTest(forceUpdate?: boolean): Promise<any> {
  const response = await GetCached({ url: `${labTest}`, forceUpdate });

  return response.data;
}

export async function saveAssessment(data: unknown): Promise<any> {
  const response = await Post(`${assessmentsRoot}`, data, true);

  return response.data;
}

export async function summarizePrescription(transcript: string): Promise<string> {
  const response = await Post(`${assessmentsRoot}/prescription-summary`, { transcript }, true);

  return response.data.summary;
}

export async function getAssessmentById(assessmentId: number): Promise<any> {
  const response = await Get(`${assessmentsRoot}/${assessmentId}`, true);
  return response.data;
}

export async function getAssessmentByUUID(assessmentId: number): Promise<any> {
  const response = await Get(`${assessmentsRoot}/byUUID/${assessmentId}`, false);
  return response.data;
}

export async function updateAssessment(
  assessmentId: number,
  assessmentData: unknown
): Promise<any> {
  const response = await Put(`${assessmentsRoot}/${assessmentId}`, assessmentData, true);
  return response.data;
}

export async function deleteAssessment(assessmentId: number): Promise<any> {
  const response = await Delete(`${assessmentsRoot}/${assessmentId}`, true);
  return response.data;
}

// asssment template APIs
export async function getAssessmentTemplates(): Promise<Array<Assessment>> {
  const response = await Get(`${assessmentTemplateRoot}`, true);
  return response.data as Array<Assessment>;
}

export async function postAssessmentTemplate(data: unknown): Promise<ApiResponse<Assessment>> {
  const response = await Post(
    `${assessmentTemplateRoot}`,
    data as unknown as Record<string, unknown>,
    true
  );
  return response.data as ApiResponse<Assessment>;
}

export async function patchAssessmentTemplate({
  assessmentData,
  assessmentId
}: {
  assessmentData: Assessment;
  assessmentId: number;
}): Promise<ApiResponse<Assessment>> {
  const response = await Patch(
    `${assessmentTemplateRoot}/${assessmentId}`,
    assessmentData as unknown as Record<string, unknown>,
    true
  );
  return response.data as ApiResponse<Assessment>;
}

export async function deleteAssessmentTemplate(assessmentId: number): Promise<ApiResponse<null>> {
  const response = await Delete(`${assessmentTemplateRoot}/${assessmentId}`, true);
  return response.data as ApiResponse<null>;
}

// assessment APIs
export async function getAssessments({
  searchValue,
  searchBy,
  page,
  pageSize,
  limit,
  from,
  until
}: {
  searchValue?: string;
  searchBy?: string;
  limit?: number;
  pageSize?: number;
  page?: number;
  from?: string;
  until?: string;
}): Promise<any> {
  const params = new URLSearchParams();
  if (page) params.append("page", page.toString());
  if (pageSize) params.append("pageSize", pageSize.toString());
  if (limit) params.append("limit", limit.toString());
  if (from) params.append("from", from);
  if (until) params.append("until", until);
  if (searchBy && searchValue) params.append("searchBy", searchBy);
  if (searchValue) params.append("searchValue", searchValue);

  const paramsString = params.toString();

  const response = await Get(`${assessmentsRoot}/?${paramsString}`, true);
  return response.data;
}

export async function getRecentAssessments(id: number): Promise<any> {
  const response = await Get(`${assessmentsRoot}/recent/?clientId="${id}`, true);
  return response.data;
}

export async function getChildren(id: number): Promise<ICD[]> {
  const response = await Get(`${diagnosisRoot}/${id}/children`, true);
  return response.data as ICD[];
}
export async function getSibblings(id: number): Promise<any> {
  const response = await Get(`${diagnosisRoot}/${id}/parents`, true);
  return response.data;
}
export async function searchDisease(diseaseName: string): Promise<ICD[]> {
  const response = await Get(`${diagnosisRoot}/search/?q=${diseaseName}`, true);

  return response.data as ICD[];
}

export async function deleteTemplateAssessment(id: number): Promise<any> {
  const response = await Delete(`${assessmentRoot}/template/${id}`, true);

  return response.data;
}

export async function searchDiagnosis(diagnosisName: string): Promise<any> {
  const response = await Get(`${diagnosisRoot}/search/?q=${diagnosisName}`, true);

  return response.data;
}

enum S3UploadDataType {
  AUDIO = "assessmentAudio",
  IMAGE = "assessmentImage"
}
type S3UploadDataTypes = ReverseMap<typeof S3UploadDataType>;

export const getS3UploadPostData = async (
  fileName: string,
  type: S3UploadDataTypes
): Promise<unknown> => {
  const response = await Post(`${assessmentRoot}/${type}`, { fileName });
  return response.data;
};

// convert base64 string to blob
export const dataURLtoBlob = (dataURL: string): Blob => {
  const byteString = atob(dataURL.split(",")[1]);
  const arrayBufferObj = new ArrayBuffer(byteString.length);
  const intArray = new Uint8Array(arrayBufferObj);
  for (let i = 0; i < byteString.length; i += 1) {
    intArray[i] = byteString.charCodeAt(i);
  }
  return new Blob([arrayBufferObj], { type: "image/svg+xml" });
};

export const uploadAssessmentImage = async (pictureData: string): Promise<unknown> => {
  const assessmentImageInfo = {
    assessmentImageKey: "",
    assessmentImage: ""
  };
  try {
    const fileName = `${Date.now()}-assessment.svg`;
    const postData = (await getS3UploadPostData(fileName, S3UploadDataType.IMAGE)) as {
      id: number;
      data: Record<string, any> & { fields: Record<string, string>; url: string };
    };
    const s3FileName = `${postData.id}/${fileName}`;
    const formData = new FormData();

    Object.entries(postData.data.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    const blobImage = dataURLtoBlob(pictureData);

    formData.append("file", blobImage, s3FileName);
    const response = await axios.post(postData.data.url, formData, {
      headers: {
        "Content-Type": "application/octet-stream"
      }
    });
    assessmentImageInfo.assessmentImage =
      response.headers.location || `${postData.data.url}/${postData.data.fields.key}` || "";
    assessmentImageInfo.assessmentImageKey = postData.data.fields.key;
    return assessmentImageInfo;
  } catch (err) {
    assessmentImageInfo.assessmentImage = "";
    assessmentImageInfo.assessmentImageKey = "";
    return assessmentImageInfo;
  }
};

export const uploadAssessmentAudio = async (blobURI: string): Promise<unknown> => {
  const assessmentAudioInfo = {
    prescriptionAudioUrl: ""
  };

  try {
    const fileName = `${Date.now()}-assessment.webm`;
    const postData = (await getS3UploadPostData(fileName, S3UploadDataType.AUDIO)) as {
      id: number;
      data: Record<string, unknown>;
    };

    const s3FileName = `${postData.id}/${fileName}`;
    const formData = new FormData();
    [
      "key",
      "bucket",
      "acl",
      "X-Amz-Algorithm",
      "X-Amz-Credential",
      "X-Amz-Date",
      "Policy",
      "X-Amz-Signature"
    ].forEach((key) => {
      formData.append(key, postData.data.fields[key]);
    });

    const blobAudio = await fetch(blobURI).then((r) => r.blob());
    formData.append("Content-Type", "audio/webm;codecs=opus");
    formData.append("file", blobAudio, s3FileName);

    await axios.post(postData.data.url, formData, {
      headers: {
        "Content-Type": "application/octet-stream"
      }
    });
    assessmentAudioInfo.prescriptionAudioUrl = `${postData.data.url}/${postData.data.fields.key}`;
    return assessmentAudioInfo;
  } catch (err) {
    return assessmentAudioInfo;
  }
};

// ---------- assessment template image apis start ----------

export const assessmentTemplateImagePost = async (data: {
  s3ResourceURL: string;
  s3ResourceKey: string;
  fileName: string;
  entityType: string;
}): Promise<ApiResponse<AttachmentInterface>> => {
  const response = await Post(assessmentTemplateImagesRoot, data, true);
  return response.data as ApiResponse<AttachmentInterface>;
};

export const getPresignedPostDataForAssessmentImageUpload = async (
  fileName: string
): Promise<any> => {
  const response = await Post(
    `${assessmentTemplateImagesRoot}/presigned-upload`,
    { fileName },
    true
  );
  return response.data;
};

export const uploadAssessmentTemplateImage = async (
  imageData: Blob,
  entityType: string
): Promise<any> => {
  const fileName = `${Date.now()}.${imageData.type.split("/")[1]}`;

  const postData = (await getPresignedPostDataForAssessmentImageUpload(fileName)) as {
    id: number;
    data: Record<string, any> & { fields: Record<string, string>; url: string };
  };

  const s3FileName = `${postData.id}/${fileName}`;

  const formData = new FormData();

  Object.entries(postData.data.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });

  formData.append("file", imageData, s3FileName);

  const response = await axios.post(postData.data.url, formData);

  const imageUrl =
    response.headers.location || `${postData.data.url}/${postData.data.fields.key}` || "";

  const imageKey = postData.data.fields.key as string;

  const savedRecord = await assessmentTemplateImagePost({
    s3ResourceURL: imageUrl,
    s3ResourceKey: imageKey,
    fileName,
    entityType
  });

  return savedRecord;
};

export async function getAssessmentTemplateImages({
  entityType = ""
}: {
  entityType?: string;
}): Promise<ApiResponse<Array<AttachmentInterface>>> {
  const params = new URLSearchParams();
  if (entityType) params.append("entityType", entityType);

  const url = `${assessmentTemplateImagesRoot}?${params.toString()}`;

  const response = await Get(url, true);
  return response.data as ApiResponse<Array<AttachmentInterface>>;
}

export const deleteAssessmentTemplateImage = async (id: number): Promise<ApiResponse<null>> => {
  const response = await Delete(`${assessmentTemplateImagesRoot}/${id}`, true);
  return response.data as ApiResponse<null>;
};

// ---------- assessment template image apis end ----------
